/* eslint-disable @typescript-eslint/naming-convention */
import { createAsyncThunk } from '@reduxjs/toolkit'

import { callErrorMsg } from 'helpers/errorMsg'
import { RootStateType } from 'store'
import { vendor } from 'config/routes'
import { saveAs } from 'file-saver'

import {
  getVendorDetails,
  postVendorRequest,
  getVendorDocumentsFileRulesAPI,
  postUploadDocumentsFile,
  getBrandList,
  getDocFileUrlAPI,
} from 'features/Enterprise/Vendor/services/addEditVendor'

import {
  type VendorDetailsInterface,
  type VMSFileType,
  type VMSDocsIdType,
} from 'features/Enterprise/Vendor/types/VendorManagement'

import { push } from 'utils/helpers/router'

import { toastSuccess, toastFailed } from 'utils/toast'

import { VendorSubmissionPayloadType } from '../../types/VendorManagement'
import { getVendorDetailRequest } from '../../services/vendorReview'
import {
  SLICE_NAME,
  setLoading,
  setFileRules,
  setDraftDetails,
  setPrincipalDetails,
  setBrandList,
} from './principalUpsertSlice'

export const fetchInvoiceUploadFileRulesPrincipal = createAsyncThunk(
  `${SLICE_NAME}/fetchInvoiceUploadFileRules`,
  async (type: VMSDocsIdType, { dispatch }) => {
    try {
      const { data } = await getVendorDocumentsFileRulesAPI(type)
      dispatch(setFileRules(data.data))
    } catch (error) {
      callErrorMsg(error)
      throw error
    }
  },
)

export const uploadVMSPrincipalDocs = createAsyncThunk(
  `${SLICE_NAME}/uploadVMSPrincipalDocs`,
  async (
    {
      file_input,
      vendor_id,
      onUploadSuccess,
    }: {
      file_input: File
      vendor_id?: number
      onUploadSuccess: (fileData: VMSFileType) => void
    },
    { dispatch, getState },
  ) => {
    const {
      auth: {
        userData: { id },
      },
    } = getState() as RootStateType
    const data: { file_input: File; created_by: string; external_id?: number } = {
      file_input,
      created_by: String(id),
    }
    if (vendor_id) data.external_id = vendor_id

    dispatch(setLoading(true))
    postUploadDocumentsFile([
      {
        typeId: vendor_id ? 'seller' : 'draft-seller',
        data,
      },
    ])
      .then((resp) => {
        if (resp[0].error) {
          toastFailed(resp[0].message)
        } else {
          onUploadSuccess({
            file_url: resp[0].files?.fileUrl,
            file_name: resp[0].files?.fileName,
            file_path: resp[0].files?.filePath,
          })
        }
      })
      .catch(() => {
        toastFailed('Terjadi kesalahan saat mengupload file')
      })
      .finally(() => {
        dispatch(setLoading(false))
      })
  },
)

export const submitPrincipalRequest = createAsyncThunk(
  `${SLICE_NAME}/submitPrincipalRequest`,
  async (
    { payload, successMessage }: { payload: VendorDetailsInterface; successMessage: string },
    { dispatch },
  ) => {
    try {
      dispatch(setLoading(true))
      await postVendorRequest(payload as unknown as VendorSubmissionPayloadType)
      toastSuccess(successMessage)
      dispatch(
        push({
          url: vendor,
          queryObject: { section: 'principal' },
        }),
      )
    } catch (error) {
      callErrorMsg(error)
    } finally {
      dispatch(setLoading(false))
    }
  },
)

export const fetchPrincipalDraft = createAsyncThunk(
  `${SLICE_NAME}/fetchPrincipalDraft`,
  async ({ vendorId }: { vendorId: string }, { dispatch }) => {
    try {
      dispatch(setLoading(true))
      const { data } = await getVendorDetailRequest(vendorId)
      const { request_data: requestData, ...restData } = data.data || {}

      dispatch(setDraftDetails(restData))
      if (restData.status === 'REJECTED') {
        dispatch(setPrincipalDetails(requestData as VendorDetailsInterface))
      }
      return { requestData: requestData || undefined, status: restData.status }
    } catch (error) {
      callErrorMsg(error)
      return false
    } finally {
      dispatch(setLoading(false))
    }
  },
)

export const fetchPrincipalDetails = createAsyncThunk(
  `${SLICE_NAME}/fetchPrincipalDetails`,
  async ({ principal_id }: { principal_id: string }, { dispatch }) => {
    try {
      dispatch(setLoading(true))
      const { data } = await getVendorDetails(principal_id)

      return data.data
    } catch (error) {
      callErrorMsg(error)
      return false
    } finally {
      dispatch(setLoading(false))
    }
  },
)

export const fetchBrandList = createAsyncThunk(
  `${SLICE_NAME}/fetchBrandList`,
  async ({ name, page_index }: { name?: string; page_index?: number }, { dispatch }) => {
    dispatch(setLoading(true))
    try {
      const { data } = await getBrandList({
        name,
        'pagination.page_index': page_index || 0,
        'pagination.page_size': 50,
        sort: 'brand_name',
        direction: 'desc',
      })

      const formattedData = data.data.map((item) => ({ id: item.brand_id, name: item.brand_name }))

      dispatch(setBrandList(formattedData))
      return formattedData
    } catch (error) {
      callErrorMsg(error)
      return []
    } finally {
      dispatch(setLoading(false))
    }
  },
)

export const fetchDocFileUrl = createAsyncThunk(
  `${SLICE_NAME}/fetchDocFileUrl`,
  async (
    {
      file: { filePath, fileName, extension },
      isDowmload,
      vendorExist,
    }: {
      file: { filePath: string; fileName: string; extension: string }
      isDowmload?: boolean
      vendorExist?: boolean
    },
    { dispatch },
  ) => {
    try {
      dispatch(setLoading(true))
      const mapFileType = {
        pdf: 'application/pdf',
        jpg: 'image/jpeg',
        jpeg: 'image/jpeg',
        png: 'image/png',
      }

      const typeId = vendorExist ? 'seller' : 'draft-seller'
      const { data } = await getDocFileUrlAPI(
        filePath,
        mapFileType[extension as keyof typeof mapFileType] || 'application/octet-stream',
        typeId,
      )

      if (isDowmload) {
        saveAs(data, fileName)
      }

      return URL.createObjectURL(
        new Blob([data], {
          type: mapFileType[extension as keyof typeof mapFileType] || 'application/octet-stream',
        }),
      )
    } catch (error) {
      callErrorMsg(error)
      throw error
    } finally {
      dispatch(setLoading(false))
    }
  },
)
